import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

interface DemoModeStatus {
  isDemoMode: boolean;
  demoRecordCounts?: Record<string, number>;
  demoParentAccounts?: Array<{
    name: string;
    email: string;
    password: string;
  }>;
}

const getDemoModeStatus = async (): Promise<DemoModeStatus> => {
  const response = await baseAPI.get<DemoModeStatus>('/demo-mode/status');
  return response.data;
};

const enableDemoMode = async (): Promise<{ message: string }> => {
  const response = await baseAPI.post<{ message: string }>(
    '/demo-mode/enable',
  );
  return response.data;
};

const disableDemoMode = async (): Promise<{ message: string }> => {
  const response = await baseAPI.post<{ message: string }>(
    '/demo-mode/disable',
  );
  return response.data;
};

/** After toggle, drop all cached studio data then hard-reload so UI matches DB. */
const refreshAppAfterDemoToggle = async (
  queryClient: ReturnType<typeof useQueryClient>,
  message: string,
) => {
  toast.success(message);
  await queryClient.cancelQueries();
  queryClient.clear();
  window.location.assign('/dashboard');
};

export const useGetDemoModeStatus = () => {
  return useQuery({
    queryKey: [KEYS.DEMO_MODE_STATUS],
    queryFn: getDemoModeStatus,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
};

export const useEnableDemoMode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: enableDemoMode,
    onSuccess: () => {
      void refreshAppAfterDemoToggle(queryClient, 'Demo mode enabled');
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ?? 'Failed to enable demo mode';
      toast.error(message);
    },
  });
};

export const useDisableDemoMode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: disableDemoMode,
    onSuccess: () => {
      void refreshAppAfterDemoToggle(queryClient, 'Demo mode disabled');
    },
    onError: () => {
      toast.error('Failed to disable demo mode');
    },
  });
};
